/**
 * Parser for the Step 2 transcript analysis Markdown contract (02-themes-and-opps.md).
 *
 * The LLM output follows a structured four-section format:
 *   SECTION A — Themes
 *   SECTION B — Opportunity Signals
 *   SECTION C — Narrative & Maturity
 *   SECTION D — Gaps, Risks & Flags
 *
 * `parseTranscriptAnalysisMarkdown` attempts full structured parsing and falls back
 * gracefully to `fallbackBlocks` when sections are missing or the format is unexpected.
 */

/** Confidence level for an opportunity signal as determined by the LLM. */
export type AnalysisConfidence = 'High' | 'Medium' | 'Low' | 'Unknown';

/** One structured theme extracted from Section A of the analysis output. */
export interface AnalysisTheme {
  number: number;
  name: string;
  summaryMarkdown: string;
  evidenceMarkdown: string;
  significanceMarkdown: string;
  alignmentMarkdown: string;
  tags: string[];
}

/** One opportunity signal extracted from Section B of the analysis output. */
export interface AnalysisSignal {
  number: number;
  label: string;
  whatWasSaidMarkdown: string;
  whatItSignalsMarkdown: string;
  potentialAiAngleMarkdown: string;
  confidence: AnalysisConfidence;
  relatedTheme: string | null;
}

/** Narrative synthesis extracted from Section C, including optional maturity scoring. */
export interface AnalysisNarrative {
  markdown: string;
  maturityClassification: 'Aware' | 'Experimenting' | 'Scaling' | 'Leading' | null;
  numericMaturityScore: number | null;
}

/** One category group from Section D (gaps, conflicting signals, scope flags, risks, or recommendations). */
export interface AnalysisGapGroup {
  kind: string;
  title: string;
  markdown: string;
  itemCount: number;
}

/** Content that could not be fully parsed — rendered as raw Markdown in the fallback view. */
export interface AnalysisFallbackBlock {
  order: number;
  title: string;
  markdown: string;
  reason: 'unknown-section' | 'partial-section' | 'parse-failure';
}

/** Complete parsed representation of the transcript analysis Markdown artifact. */
export interface TranscriptAnalysisReviewModel {
  filename: string;
  themes: AnalysisTheme[];
  signals: AnalysisSignal[];
  narrative: AnalysisNarrative | null;
  gapGroups: AnalysisGapGroup[];
  /** Section D heading derived from the em-dash format; null for legacy colon-format artifacts. */
  sectionDTitle: string | null;
  fallbackBlocks: AnalysisFallbackBlock[];
  distinctStakeholderCount: number | null;
  isStructured: boolean;
  hasCompleteContract: boolean;
}

const DEFAULT_FILENAME = '02-themes-and-opps.md';
// Matches both `## SECTION A: Title` (legacy colon) and `## Section A — Title` (em-dash)
const SECTION_PATTERN = /^##\s+SECTION\s+([A-D])(?::|\s*[—–-]+)\s*(.+?)\s*$/i;

interface SectionBlock {
  key: string;
  title: string;
  markdown: string;
  order: number;
}

/**
 * Parse the Step 2 transcript analysis artifact into a structured review model.
 *
 * Attempts full four-section parsing (A–D). Sections that are absent or malformed
 * are captured in `fallbackBlocks` so the UI can always render something meaningful.
 *
 * @param markdown - Raw LLM Markdown output; null/undefined yields an empty model.
 * @param filename - Download filename to embed in the model (defaults to '02-themes-and-opps.md').
 * @returns A fully typed TranscriptAnalysisReviewModel with `hasCompleteContract` indicating parse quality.
 */
export function parseTranscriptAnalysisMarkdown(
  markdown: string | null | undefined,
  filename = DEFAULT_FILENAME,
): TranscriptAnalysisReviewModel {
  const source = (markdown ?? '').replaceAll(/\r\n?/g, '\n').trim();
  const empty = createEmptyModel(filename);
  if (!source) {
    return empty;
  }

  const { sections, outside } = splitSections(source);
  if (sections.length === 0) {
    return {
      ...empty,
      fallbackBlocks: [
        { order: 0, title: 'Unparsed analysis', markdown: source, reason: 'parse-failure' },
      ],
    };
  }

  const uniqueCanonical = new Map<string, SectionBlock>();
  const fallbacks: AnalysisFallbackBlock[] = outside.map((block, index) => ({
    order: index,
    title: 'Additional analysis content',
    markdown: block,
    reason: 'unknown-section',
  }));
  sections.forEach((section) => {
    if (uniqueCanonical.has(section.key)) {
      fallbacks.push({
        order: section.order,
        title: section.title,
        markdown: section.markdown,
        reason: 'unknown-section',
      });
    } else {
      uniqueCanonical.set(section.key, section);
    }
  });

  const a = uniqueCanonical.get('A');
  const b = uniqueCanonical.get('B');
  const c = uniqueCanonical.get('C');
  const d = uniqueCanonical.get('D');
  const themes = a ? parseThemes(a.markdown, fallbacks, a.order) : [];
  const signals = b ? parseSignals(b.markdown, fallbacks, b.order) : [];
  const narrative = c ? parseNarrative(c.markdown) : null;
  const gapGroups = d ? parseGapGroups(d.markdown) : [];
  // Extract Section D title only for new em-dash format; legacy colon format returns null so
  // the component falls back to its hardcoded heading ('Gaps & Conflicting Signals').
  const sectionDTitle: string | null = d
    ? /[—–]/.test(d.title)
      ? d.title.replace(/^##\s+SECTION\s+[A-D]\s*[—–]+\s*/i, '').trim() || null
      : null
    : null;
  const stakeholderCount = countStakeholders(themes, signals);
  const inCanonicalOrder =
    sections
      .filter((section) => ['A', 'B', 'C', 'D'].includes(section.key))
      .map((section) => section.key)
      .join('') === 'ABCD';
  const complete = Boolean(
    a &&
    b &&
    c &&
    d &&
    themes.length &&
    signals.length &&
    narrative?.markdown &&
    gapGroups.length &&
    inCanonicalOrder &&
    fallbacks.every((block) => block.reason !== 'partial-section'),
  );

  return {
    filename,
    themes,
    signals,
    narrative,
    gapGroups,
    sectionDTitle,
    fallbackBlocks: fallbacks.sort((left, right) => left.order - right.order),
    distinctStakeholderCount: stakeholderCount || null,
    isStructured: true,
    hasCompleteContract: complete,
  };
}

/** Return an empty model with all arrays set to [] and all flags false. */
function createEmptyModel(filename: string): TranscriptAnalysisReviewModel {
  return {
    filename,
    themes: [],
    signals: [],
    narrative: null,
    gapGroups: [],
    sectionDTitle: null,
    fallbackBlocks: [],
    distinctStakeholderCount: null,
    isStructured: false,
    hasCompleteContract: false,
  };
}

/** Split the raw Markdown into labelled SECTION A–D blocks and any content that falls outside them. */
function splitSections(source: string): { sections: SectionBlock[]; outside: string[] } {
  const sections: SectionBlock[] = [];
  const outside: string[] = [];
  let current: SectionBlock | null = null;
  let buffer: string[] = [];
  const flush = (): void => {
    const body = buffer.join('\n').trim();
    if (current) {
      sections.push({ ...current, markdown: body });
    } else if (body) {
      outside.push(body);
    }
    buffer = [];
  };
  source.split('\n').forEach((line, index) => {
    const match = SECTION_PATTERN.exec(line.trim());
    if (match) {
      flush();
      current = { key: match[1].toUpperCase(), title: line.trim(), markdown: '', order: index };
    } else {
      buffer.push(line);
    }
  });
  flush();
  return { sections, outside };
}

/** Extract tag strings from a theme block body or fall back to bold terms in the summary. */
function extractTags(blockBody: string, summaryMarkdown: string): string[] {
  const tagsLine = /\*\*Tags?:?\*\*\s*:?\s*(.+)$/im.exec(blockBody);
  if (tagsLine) {
    return tagsLine[1]
      .split(',')
      .map((t) => t.trim())
      .filter(Boolean);
  }
  const boldTerms: string[] = [];
  const pattern = /\*\*([^*]+)\*\*/g;
  let match: RegExpExecArray | null;
  while ((match = pattern.exec(summaryMarkdown)) !== null && boldTerms.length < 4) {
    boldTerms.push(match[1].trim());
  }
  return boldTerms.slice(0, 4);
}

/** Parse Section A into AnalysisTheme entries; pushes partial entries to fallbacks. */
function parseThemes(
  markdown: string,
  fallbacks: AnalysisFallbackBlock[],
  order: number,
): AnalysisTheme[] {
  // New bullet-list format: `- **Theme N: Name** — inline description`
  if (/^-\s+\*\*Theme\s+\d+/im.test(markdown)) {
    const blocks = splitRepeatedBlocks(markdown, /^-\s+\*\*Theme\s+(\d+):\s*(.+?)\*\*/i);
    return blocks.map((block) => ({
      number: block.number,
      name: block.label,
      summaryMarkdown: block.body,
      evidenceMarkdown: '',
      significanceMarkdown: '',
      alignmentMarkdown: '',
      tags: extractTags(block.body, block.body),
    }));
  }
  // Legacy labeled-field format: standalone `**Theme N: Name**` with sub-fields
  const blocks = splitRepeatedBlocks(markdown, /^\*\*Theme\s+(\d+):\s*(.+?)\*\*\s*$/i);
  return blocks.map((block) => {
    const fields = parseFields(block.body, [
      'Summary',
      'Evidence',
      'Significance',
      'Stakeholder alignment',
    ]);
    if (fields.missing.length) {
      fallbacks.push({
        order,
        title: `Partial Theme ${block.number}`,
        markdown: block.raw,
        reason: 'partial-section',
      });
    }
    return {
      number: block.number,
      name: block.label,
      summaryMarkdown: fields.values.Summary ?? '',
      evidenceMarkdown: fields.values.Evidence ?? '',
      significanceMarkdown: fields.values.Significance ?? '',
      alignmentMarkdown: fields.values['Stakeholder alignment'] ?? '',
      tags: extractTags(block.body, fields.values.Summary ?? ''),
    };
  });
}

/** Parse Section B into AnalysisSignal entries; pushes entries missing core fields to fallbacks. */
function parseSignals(
  markdown: string,
  fallbacks: AnalysisFallbackBlock[],
  order: number,
): AnalysisSignal[] {
  // New bullet-list format: `- **Signal N: Name** — inline description with labeled sub-sections`
  if (/^-\s+\*\*Signal\s+\d+/im.test(markdown)) {
    const blocks = splitRepeatedBlocks(markdown, /^-\s+\*\*Signal\s+(\d+):\s*(.+?)\*\*/i);
    return blocks.map((block, index) => {
      // Infer confidence from signal position when no explicit Confidence field exists
      let positionConfidence: AnalysisConfidence;
      if (index < 2) {
        positionConfidence = 'High';
      } else if (index < 4) {
        positionConfidence = 'Medium';
      } else {
        positionConfidence = 'Low';
      }
      const immediateMatch = /Immediate Action Required\s*:/i.exec(block.body);
      const businessMatch = /Business Case\s*:/i.exec(block.body);
      const descEnd = immediateMatch?.index ?? businessMatch?.index ?? block.body.length;
      const actionEnd = businessMatch?.index ?? block.body.length;
      return {
        number: block.number,
        label: block.label,
        whatWasSaidMarkdown: block.body.slice(0, descEnd).trim(),
        whatItSignalsMarkdown: immediateMatch
          ? block.body.slice(immediateMatch.index + immediateMatch[0].length, actionEnd).trim()
          : '',
        potentialAiAngleMarkdown: businessMatch
          ? block.body.slice(businessMatch.index + businessMatch[0].length).trim()
          : '',
        confidence: positionConfidence,
        relatedTheme: null,
      };
    });
  }
  // Legacy labeled-field format: standalone `**Signal N: Name**` with sub-fields
  const blocks = splitRepeatedBlocks(markdown, /^\*\*Signal\s+(\d+):\s*(.+?)\*\*\s*$/i);
  return blocks.map((block) => {
    const fields = parseFields(block.body, [
      'What was said',
      'What it signals',
      'Potential AI angle',
      'Confidence',
      'Related theme',
    ]);
    if (fields.missing.slice(0, 3).length) {
      fallbacks.push({
        order,
        title: `Partial Signal ${block.number}`,
        markdown: block.raw,
        reason: 'partial-section',
      });
    }
    const confidenceValue = fields.values.Confidence?.trim();
    const confidence: AnalysisConfidence =
      confidenceValue === 'High' || confidenceValue === 'Medium' || confidenceValue === 'Low'
        ? confidenceValue
        : 'Unknown';
    return {
      number: block.number,
      label: block.label,
      whatWasSaidMarkdown: fields.values['What was said'] ?? '',
      whatItSignalsMarkdown: fields.values['What it signals'] ?? '',
      potentialAiAngleMarkdown: fields.values['Potential AI angle'] ?? '',
      confidence,
      relatedTheme: fields.values['Related theme']?.trim() || null,
    };
  });
}

/** Split a section body into numbered repeating blocks identified by the anchor pattern.
 *  Also captures inline text following the matched anchor on the same line (bullet-list format). */
function splitRepeatedBlocks(
  markdown: string,
  anchor: RegExp,
): Array<{ number: number; label: string; body: string; raw: string }> {
  const results: Array<{ number: number; label: string; body: string; raw: string }> = [];
  let current: { number: number; label: string; lines: string[]; anchor: string } | null = null;
  const flush = (): void => {
    if (!current) {
      return;
    }
    // Strip trailing horizontal-rule markers (`---`) that skill output places between sections
    const body = current.lines
      .join('\n')
      .trim()
      .replace(/\n+\s*-{3,}\s*$/, '')
      .trim();
    results.push({
      number: current.number,
      label: current.label,
      body,
      raw: `${current.anchor}\n${body}`.trim(),
    });
  };
  markdown.split('\n').forEach((line) => {
    const trimmedLine = line.trim();
    const match = anchor.exec(trimmedLine);
    if (match) {
      flush();
      // Capture any inline body on the same line after the matched anchor (bullet-list format)
      const afterMatch = trimmedLine
        .slice(match[0].length)
        .replace(/^\s*(?:—|–|-{1,3})\s*/, '')
        .trim();
      current = {
        number: Number(match[1]),
        label: match[2].trim(),
        lines: afterMatch ? [afterMatch] : [],
        anchor: line,
      };
    } else if (current) {
      current.lines.push(line);
    }
  });
  flush();
  return results;
}

/** Extract named field values from a Markdown block using bold-label line format. */
function parseFields(
  markdown: string,
  names: string[],
): { values: Record<string, string>; missing: string[] } {
  const values: Record<string, string> = {};
  let active: string | null = null;
  markdown.split('\n').forEach((line) => {
    const match =
      /^\s*(?:[-*]\s*)?\*\*([^*]+?)\*\*\s*:\s*(.*)$/.exec(line) ??
      /^\s*(?:[-*]\s*)?([^:]+):\s*(.*)$/.exec(line);
    const name = match
      ? names.find((candidate) => candidate.toLowerCase() === match[1].trim().toLowerCase())
      : undefined;
    if (match && name) {
      active = name;
      values[name] = match[2].trim();
    } else if (active && line.trim()) {
      values[active] = `${values[active]}\n${line}`.trim();
    }
  });
  return { values, missing: names.filter((name) => !values[name]?.trim()) };
}

/** Parse Section C into an AnalysisNarrative, extracting maturity classification and score when present. */
function parseNarrative(markdown: string): AnalysisNarrative {
  const classification = /\b(Aware|Experimenting|Scaling|Leading)\b/i.exec(markdown)?.[1];
  const score = /(?:maturity\s+score|score)\s*[:=-]\s*(\d+(?:\.\d+)?)/i.exec(markdown)?.[1];
  const normalized = classification
    ? (`${classification[0].toUpperCase()}${classification.slice(1).toLowerCase()}` as AnalysisNarrative['maturityClassification'])
    : null;
  return {
    markdown: markdown.trim(),
    maturityClassification: normalized,
    numericMaturityScore: score ? Number(score) : null,
  };
}

/** Parse Section D into AnalysisGapGroup entries.
 *  Supports two formats: new `### Sub-section` recommendation style, and legacy bold-label gap style. */
function parseGapGroups(markdown: string): AnalysisGapGroup[] {
  // New format: `### Title` or `### **Title**` sub-section headers
  if (/^###\s+/m.test(markdown)) {
    const lines = markdown.split('\n');
    const groups: AnalysisGapGroup[] = [];
    let current: { kind: string; title: string; lines: string[] } | null = null;
    const flush = (): void => {
      if (!current) {
        return;
      }
      const body = current.lines.join('\n').trim();
      groups.push({
        kind: current.kind,
        title: current.title,
        markdown: body,
        itemCount: current.lines.filter((line) => /^\s*(?:[-*+]|\d+\.)\s+/.test(line)).length,
      });
    };
    lines.forEach((line) => {
      const headerMatch = /^###\s+\*{0,2}(.+?)\*{0,2}\s*$/.exec(line.trim());
      if (headerMatch) {
        flush();
        const title = headerMatch[1].trim();
        const kind = title
          .toLowerCase()
          .replaceAll(/[^a-z0-9]+/g, '-')
          .replaceAll(/^-+|-+$/g, '');
        current = { kind, title, lines: [] };
      } else if (current) {
        current.lines.push(line);
      }
    });
    flush();
    return groups;
  }
  // Legacy format: bold-label gap categories (Data Gaps, Conflicting Signals, etc.)
  const definitions: Array<[string, string]> = [
    ['data-gaps', 'Data Gaps'],
    ['conflicting-signals', 'Conflicting Signals'],
    ['scope-flags', 'Scope Flags'],
    ['risks', 'Risks to Watch'],
  ];
  const lines = markdown.split('\n');
  const groups: AnalysisGapGroup[] = [];
  let current: { kind: string; title: string; lines: string[] } | null = null;
  const flush = (): void => {
    if (!current) {
      return;
    }
    const body = current.lines.join('\n').trim();
    groups.push({
      kind: current.kind,
      title: current.title,
      markdown: body,
      itemCount: current.lines.filter((line) => /^\s*[-*+]\s+/.test(line)).length,
    });
  };
  lines.forEach((line) => {
    const normalized = line
      .replaceAll(/^#+\s*|\*\*/g, '')
      .replace(/:\s*$/, '')
      .trim()
      .toLowerCase();
    const definition = definitions.find(([, title]) => title.toLowerCase() === normalized);
    if (definition) {
      flush();
      current = { kind: definition[0], title: definition[1], lines: [] };
    } else if (current) {
      current.lines.push(line);
    }
  });
  flush();
  return groups;
}

/** Count distinct stakeholder names mentioned across theme evidence and signal evidence blocks. */
function countStakeholders(themes: AnalysisTheme[], signals: AnalysisSignal[]): number {
  const names = new Set<string>();
  const content = [
    ...themes.map((theme) => theme.evidenceMarkdown),
    ...signals.map((signal) => signal.whatWasSaidMarkdown),
  ];
  content.forEach((text) => {
    const matches = text.matchAll(/(?:stakeholder|source)\s*:\s*([^;\n]+)/gi);
    for (const match of matches) {
      names.add(match[1].trim().replaceAll(/\s+/g, ' ').toLowerCase());
    }
  });
  return names.size;
}
